import { useEffect, useMemo, useState } from "react";
import DeletePopup from "components/popups/DeletePopup";
import GlobalTable from "components/shared/table/GlobalTable";
import DatasourceEditorPopup from "./datasource/DatasourceEditorPopup";
import {
  useCreateDataSourceMutation,
  useDeleteDataSourceMutation,
  useGetDataSourcesQuery,
  useUpdateDataSourceMutation,
} from "store/api";
import useTranslation from "utils/hooks/useTranslation";

type DataSourceRecord = Record<string, any>;
type DatasourceTableQuery = {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: "ascend" | "descend";
  filters?: Record<string, unknown>;
  search?: string;
};

const defaultPageSize = 10;

const DatasourcePage: React.FC = () => {
  const { t } = useTranslation();
  const {
    data: dataSources = [],
    isFetching,
    isLoading,
    refetch,
  } = useGetDataSourcesQuery("", {
    refetchOnMountOrArgChange: true,
  });
  const datasourceListLabel = t("list_datasource");
  const [rows, setRows] = useState<DataSourceRecord[]>([]);
  const [editingRow, setEditingRow] = useState<DataSourceRecord | null>(null);
  const [isEditorPopupOpen, setIsEditorPopupOpen] = useState(false);
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | number | null>(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [createDataSource, { isLoading: isCreatingDatasource }] = useCreateDataSourceMutation();
  const [updateDataSource, { isLoading: isUpdatingDatasource }] = useUpdateDataSourceMutation();
  const [deleteDataSource, { isLoading: isDeletingDatasource }] = useDeleteDataSourceMutation();
  const [tableQuery, setTableQuery] = useState<DatasourceTableQuery>({
    page: 1,
    pageSize: defaultPageSize,
    search: "",
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const nextRows = Array.isArray(dataSources) ? dataSources : [];
    setRows(nextRows);
    setTableQuery((current) => ({
      ...current,
      page: 1,
      pageSize: current.pageSize || defaultPageSize,
    }));
  }, [dataSources]);

  const renderCellValue = (value: unknown) => {
    if (value == null || value === "") return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
      return String(value);
    }
    if (typeof value === "object") return JSON.stringify(value);
    return "";
  };

  const columns = useMemo(
    () => [
      {
        key: "datasourceId",
        title: "ID",
        dataIndex: "datasourceId",
        width: 80,
        render: (_: unknown, row: DataSourceRecord) => renderCellValue(row.id),
      },
      {
        key: "name",
        title: t("name"),
        dataIndex: "name",
        width: 180,
      },
      {
        key: "type",
        title: t("type") || "Type",
        dataIndex: "type",
        width: 120,
      },
      {
        key: "format",
        title: t("format") || "Format",
        dataIndex: "format",
        width: 120,
      },
      {
        key: "baseUrl",
        title: "Base URL",
        dataIndex: "baseUrl",
        width: 260,
      },
      {
        key: "createdBy",
        title: "Created by",
        dataIndex: "createdBy",
        width: 140,
        render: renderCellValue,
      },
      {
        key: "updatedBy",
        title: "Updated by",
        dataIndex: "updatedBy",
        width: 140,
        render: renderCellValue,
      },
      {
        key: "createdAt",
        title: "Created at",
        dataIndex: "createdAt",
        width: 180,
        render: renderCellValue,
      },
      {
        key: "updatedAt",
        title: "Updated at",
        dataIndex: "updatedAt",
        width: 180,
        render: renderCellValue,
      },
    ],
    [t]
  );

  const columnsShifted = useMemo(
    () =>
      columns.map((column: any) => ({
        ...column,
        onCell: () => ({ style: { paddingInlineStart: "12px", borderInlineEnd: "1px solid #e5e7eb" } }),
        onHeaderCell: () => ({ style: { paddingInlineStart: "12px", borderInlineEnd: "1px solid #e5e7eb" } }),
      })),
    [columns]
  );

  const requestDeleteRow = (rowId: string | number) => {
    setPendingDeleteRowId(rowId);
    setIsDeletePopupOpen(true);
  };

  const openCreatePopup = () => {
    setEditingRow(null);
    setIsEditorPopupOpen(true);
  };

  const openEditPopup = (row: DataSourceRecord) => {
    setEditingRow(row);
    setIsEditorPopupOpen(true);
  };

  const closeEditorPopup = () => {
    setIsEditorPopupOpen(false);
    setEditingRow(null);
  };
  const saveDatasource = async (payload: Record<string, unknown>) => {
    try {
      if (editingRow?.id == null) {
        await createDataSource(payload).unwrap();
      } else {
        await updateDataSource({ id: editingRow.id, data: payload }).unwrap();
      }
      closeEditorPopup();
      await refetch();
    } catch {
      // handled by api interceptor
    }
  };

  const confirmDeleteRow = async () => {
    if (pendingDeleteRowId == null) return;
    try {
      await deleteDataSource({ id: pendingDeleteRowId }).unwrap();
      setRows((current) => current.filter((row) => row.id !== pendingDeleteRowId));
      setEditingRow((current) => (current?.id === pendingDeleteRowId ? null : current));
      setPendingDeleteRowId(null);
      setIsDeletePopupOpen(false);
    } catch {
      // keep popup open on API error so user can retry/cancel
    }
  };

  const rowActions = useMemo(
    () => [
      {
        key: "edit",
        label: "Edit datasource",
        icon: "edit",
        ariaLabel: "Edit datasource",
        onClick: (row: DataSourceRecord) => openEditPopup(row),
      },
      {
        key: "delete",
        label: "Delete datasource",
        icon: "delete",
        ariaLabel: "Delete datasource",
        danger: true,
        onClick: (row: DataSourceRecord) => requestDeleteRow(row.id),
      },
    ],
    []
  );

  const isSavingDatasource = isCreatingDatasource || isUpdatingDatasource;

  return (
    <group data-height="auto" data-min-height="fit">
      <group data-direction="column" data-gap="10" data-space="10">
        <GlobalTable<DataSourceRecord>
        rowKey="id"
        columns={columnsShifted as any}
        data={rows}
        mode="client"
        bordered
        loading={isLoading || isFetching}
        searchable
        searchPlaceholder="Rechercher"
        tableTitle={datasourceListLabel === "list_datasource" ? "list_datasource" : datasourceListLabel}
        emptyText="No datasources found"
        rowActions={rowActions}
        onNew={openCreatePopup}
        query={tableQuery}
        onQueryChange={(next) => {
            setTableQuery((current) => ({
            ...current,
            ...next,
            }));
        }}
        scroll={{ x: "max-content" }}
        />

        <DatasourceEditorPopup
          isOpen={isEditorPopupOpen}
          editingRow={editingRow}
          isSaving={isSavingDatasource}
          onClose={closeEditorPopup}
          onSave={saveDatasource}
        />

        <DeletePopup
          isOpen={isDeletePopupOpen}
          closePopup={() => {
            setIsDeletePopupOpen(false);
            setPendingDeleteRowId(null);
          }}
          onDelete={confirmDeleteRow}
          message={t("Are you sure you want to delete?")}
          isLoading={isDeletingDatasource}
        />
      </group>
    </group>
  );
};

export default DatasourcePage;